/**
 * Charts — Volume trend bar chart, Response time histogram, Sent/Received donut.
 */

import React from 'react';
import { Typography, Empty } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { BarChart3, Timer, PieChart } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  ArcElement, Tooltip, Legend, Title as ChartTitle,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FADE_UP } from '../constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, ChartTitle);

const { Text } = Typography;

function cardStyle(isDark) {
  return {
    borderRadius: 14,
    background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
    boxShadow: isDark
      ? '0 1px 3px rgba(0,0,0,0.2)'
      : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };
}

function CardHeader({ icon: Icon, color, title, isDark }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: isDark ? `${color}18` : `${color}10`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} color={color} strokeWidth={2} />
      </div>
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
    </div>
  );
}

function getBarOptions(isDark) {
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const tickColor = isDark ? '#6b7280' : '#9ca3af';
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: tickColor,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: { size: 11, weight: 500 },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: isDark ? '#1f2937' : '#fff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#6b7280',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        bodyFont: { size: 12 },
        titleFont: { size: 12, weight: 600 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: tickColor, font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: tickColor, font: { size: 11 } },
        grid: { color: gridColor, drawBorder: false },
        border: { display: false },
      },
    },
  };
}

// ── Volume Trend ─────────────────────────────────────────────────────────────
function VolumeTrendChart({ volumeTrend, isDark }) {
  const labels = volumeTrend.map((d) => {
    const date = new Date(d.date + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const data = {
    labels,
    datasets: [
      {
        label: 'Received',
        data: volumeTrend.map((d) => d.received),
        backgroundColor: isDark ? 'rgba(99,102,241,0.7)' : 'rgba(99,102,241,0.65)',
        hoverBackgroundColor: 'rgba(99,102,241,0.9)',
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
      {
        label: 'Sent',
        data: volumeTrend.map((d) => d.sent),
        backgroundColor: isDark ? 'rgba(16,185,129,0.7)' : 'rgba(16,185,129,0.65)',
        hoverBackgroundColor: 'rgba(16,185,129,0.9)',
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
    ],
  };

  return volumeTrend.length === 0 ? (
    <Empty description="No volume data" />
  ) : (
    <div style={{ height: 230, flex: 1 }}>
      <Bar data={data} options={getBarOptions(isDark)} />
    </div>
  );
}

// ── Response Histogram ───────────────────────────────────────────────────────
function ResponseHistogramChart({ histogram, isDark }) {
  const hasData = histogram.some((b) => b.count > 0);

  const data = {
    labels: histogram.map((b) => b.label),
    datasets: [{
      label: 'Replies',
      data: histogram.map((b) => b.count),
      backgroundColor: isDark ? 'rgba(245,158,11,0.7)' : 'rgba(245,158,11,0.65)',
      hoverBackgroundColor: 'rgba(245,158,11,0.9)',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };

  const options = {
    ...getBarOptions(isDark),
    plugins: {
      ...getBarOptions(isDark).plugins,
      legend: { display: false },
    },
  };

  return !hasData ? (
    <Empty description="No reply data" />
  ) : (
    <div style={{ height: 230, flex: 1 }}>
      <Bar data={data} options={options} />
    </div>
  );
}

// ── Donut ────────────────────────────────────────────────────────────────────
function SentReceivedDonut({ totalReceived, totalSent, isDark }) {
  const tickColor = isDark ? '#6b7280' : '#9ca3af';
  const data = {
    labels: ['Received', 'Sent'],
    datasets: [{
      data: [totalReceived, totalSent],
      backgroundColor: ['rgba(99,102,241,0.75)', 'rgba(16,185,129,0.75)'],
      hoverBackgroundColor: ['rgba(99,102,241,0.9)', 'rgba(16,185,129,0.9)'],
      borderWidth: 0,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: tickColor,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: { size: 12, weight: 500 },
        },
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#fff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#6b7280',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString()}` },
      },
    },
    cutout: '68%',
  };

  return (totalReceived + totalSent) === 0 ? (
    <Empty description="No email data" />
  ) : (
    <div style={{ height: 230, flex: 1 }}>
      <Doughnut data={data} options={options} />
    </div>
  );
}

// ── Exported Charts container ────────────────────────────────────────────────
export function Charts({ metrics, isDark }) {
  const reduced = useReducedMotion();
  const anim = reduced ? {} : FADE_UP;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
      {/* Volume trend (wider) */}
      <motion.div variants={anim} initial="hidden" animate="visible">
        <div style={cardStyle(isDark)}>
          <CardHeader icon={BarChart3} color="#6366f1" title="Email Volume Trend" isDark={isDark} />
          <VolumeTrendChart volumeTrend={metrics.volumeTrend} isDark={isDark} />
        </div>
      </motion.div>

      {/* Response histogram */}
      <motion.div variants={anim} initial="hidden" animate="visible">
        <div style={cardStyle(isDark)}>
          <CardHeader icon={Timer} color="#f59e0b" title="Response Time" isDark={isDark} />
          <ResponseHistogramChart histogram={metrics.responseHistogram} isDark={isDark} />
        </div>
      </motion.div>

      {/* Donut */}
      <motion.div variants={anim} initial="hidden" animate="visible">
        <div style={cardStyle(isDark)}>
          <CardHeader icon={PieChart} color="#10b981" title="Sent vs Received" isDark={isDark} />
          <SentReceivedDonut totalReceived={metrics.totalReceived} totalSent={metrics.totalSent} isDark={isDark} />
        </div>
      </motion.div>
    </div>
  );
}
